import { useState, useEffect } from 'react';
import { Spinner } from './Spinner';
import { getLeadActivities } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';

const activityIcons = {
  status_change: (
    <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
      <path d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
  ),
  lead_created: (
    <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
      <path d="M12 4v16m8-8H4" />
    </svg>
  ),
  lead_updated: (
    <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
      <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  note_added: (
    <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
      <path d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
    </svg>
  ),
  assignment_changed: (
    <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
      <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
};

const activityColors = {
  status_change: 'bg-blue-100 text-blue-600',
  lead_created: 'bg-green-100 text-green-600',
  lead_updated: 'bg-yellow-100 text-yellow-600',
  note_added: 'bg-purple-100 text-purple-600',
  assignment_changed: 'bg-orange-100 text-orange-600',
};

export const ActivityTimeline = ({ leadId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    if (leadId) {
      fetchActivities();
    }
  }, [leadId]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const data = await getLeadActivities(leadId);
      setActivities(data);
    } catch (error) {
      toast.error(t('failedToLoadActivityTimeline'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return t('justNow');
    if (diffInMinutes === 1) return `${t('minuteAgo')}`;
    if (diffInMinutes < 60) return `${diffInMinutes} ${t('minutesAgo')}`;
    if (diffInHours === 1) return `${t('hourAgo')}`;
    if (diffInHours < 24) return `${diffInHours} ${t('hoursAgo')}`;
    if (diffInDays === 1) return `${t('dayAgo')}`;
    if (diffInDays < 7) return `${diffInDays} ${t('daysAgo')}`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const renderActivityDescription = (activity) => {
    const details = activity.details || {};

    switch (activity.activity_type) {
      case 'status_change':
        return (
          <span>
            {t('statusChange')} <span className="font-semibold">{details.from || 'Unknown'}</span> {t('to')}{' '}
            <span className="font-semibold">{details.to || 'Unknown'}</span>
          </span>
        );
      case 'lead_created':
        return <span>{t('leadCreatedActivity')}</span>;
      case 'lead_updated':
        return <span>{t('leadUpdatedActivity')}</span>;
      case 'note_added':
        return (
          <div>
            <span>{t('noteAdded')}</span>
            {details.note && (
              <p className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded border border-gray-200">
                {details.note}
              </p>
            )}
          </div>
        );
      case 'assignment_changed':
        return (
          <span>
            {t('assignmentChanged')} <span className="font-semibold">{details.assigned_to || 'Unassigned'}</span>
          </span>
        );
      default:
        return <span>{activity.activity_type ? activity.activity_type.replace(/_/g, ' ') : 'Unknown activity'}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="md" />
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8">
        <svg
          className="w-12 h-12 mx-auto mb-3 text-gray-300"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-gray-500">{t('noActivity')}</p>
      </div>
    );
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {activities.map((activity, activityIdx) => (
          <li key={activity.id}>
            <div className="relative pb-8">
              {activityIdx !== activities.length - 1 && (
                <span
                  className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                  aria-hidden="true"
                />
              )}
              <div className="relative flex items-start space-x-3">
                <div>
                  <div
                    className={`
                      relative flex h-10 w-10 items-center justify-center rounded-full
                      ${activityColors[activity.activity_type] || 'bg-gray-100 text-gray-600'}
                    `}
                  >
                    {activityIcons[activity.activity_type] || activityIcons.lead_updated}
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div>
                    <div className="text-sm">
                      <span className="font-medium text-gray-900">
                        {activity.user_name || t('system')}
                      </span>{' '}
                      {renderActivityDescription(activity)}
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {formatDate(activity.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
